import { z } from 'zod';
/**
 * ISO-3166 alpha-2, uppercase. The single definition of the country-code
 * contract: the stored form (`users.place_country_code`, checked by
 * `users_place_country_code_format`), the place payload below, the member's
 * country-confirmation write, and feature-flag country targeting all parse
 * through this, so a value that validates anywhere is storable everywhere.
 */
export const CountryCodeSchema = z
    .string()
    .regex(/^[A-Z]{2}$/, 'Must be ISO-3166 alpha-2 uppercase');
/**
 * The longest place text the platform stores or filters by: the place payload
 * below, and the Circles and events directories' `placeText` filters (the API
 * answers 422 past it; the web Circles directory reads a longer URL value as
 * absent).
 */
export const PLACE_TEXT_MAX = 240;
/**
 * Structured place payload accepted on write paths that used to take a
 * free-text location field. Sourced from the client's PlacePicker, which
 * resolves it via `/api/v1/geo/places/:id` (Google Places). The server
 * trusts the payload by contract (validate at boundary) and stores all
 * five fields without re-fetching from Google.
 */
export const PlacePayloadSchema = z.object({
    placeId: z.string().min(1),
    placeText: z.string().min(1).max(PLACE_TEXT_MAX),
    placeLat: z.number().min(-90).max(90),
    placeLng: z.number().min(-180).max(180),
    placeCountryCode: CountryCodeSchema.nullable(),
});
/**
 * Client-facing place projection. Read endpoints return this object as
 * a single nullable field on the entity (`null` when no place is set).
 * Keep the field names un-prefixed here — `place.id` reads cleanly,
 * `place.placeId` does not.
 */
export const PlaceViewSchema = z.object({
    id: z.string(),
    text: z.string(),
    lat: z.number(),
    lng: z.number(),
    countryCode: z.string().nullable(),
});
export const LOCATION_DETAILS_MAX_LENGTH = 500;
/**
 * An event's in-person venue: the geocoded place plus what a map pin can't
 * carry. `placeName` is the venue's own name ("The Battery") when the picked
 * place is an establishment — null for a bare street address.
 * `locationDetails` is the organizer's free text for finding the room
 * (floor, cross streets, entrance). Both ride inside the place so neither
 * can exist on an event without one.
 *
 * Optional on the wire so clients that predate the two fields keep writing.
 * `null` clears a field. Absent leaves it unset on create; on update it
 * keeps the stored value while the place is unchanged and clears it when
 * the place changes.
 */
export const VenuePayloadSchema = PlacePayloadSchema.extend({
    placeName: z.string().min(1).max(240).nullable().optional(),
    locationDetails: z
        .string()
        .trim()
        .min(1)
        .max(LOCATION_DETAILS_MAX_LENGTH)
        .nullable()
        .optional(),
});
export const VenueViewSchema = PlaceViewSchema.extend({
    name: z.string().nullable(),
    details: z.string().nullable(),
});
//# sourceMappingURL=place.js.map